import { useEffect, useRef, useState } from "react";
import { useInventory } from "../../../InventoryProvider";
import { InventoryData } from "../../../../../types/inventoryData";
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  Pagination,
  Typography,
} from "@mui/material";
import {
  getProductStatusColor,
  getSelectedRows,
} from "../../../../../helpers/utils";
import { EditRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const InventoryPhoneData = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const {
    rows,
    filteredRows,
    setFilteredRows,
    selectedFilterValues,
    headCells,
    setSelectedProduct,
  } = useInventory();
  const { t } = useTranslation();

  const handleSelectAllCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked && rows) {
      setFilteredRows((prev) => {
        const newFilteredRows = prev.map((oldrow) => {
          return {
            ...oldrow,
            isSelected: true,
          };
        });
        return newFilteredRows;
      });
    } else {
      setFilteredRows((prev) => {
        const newFilteredRows = prev.map((oldrow) => {
          return {
            ...oldrow,
            isSelected: false,
          };
        });
        return newFilteredRows;
      });
    }
  };

  const handleCheckboxClick = (id: number) => {
    setFilteredRows((prev) => {
      const newFilteredRows = prev.map((oldrow) => {
        if (oldrow.productId == id) {
          return {
            ...oldrow,
            isSelected: !oldrow.isSelected,
          };
        } else return oldrow;
      });
      return newFilteredRows;
    });
  };

  const myRef = useRef<HTMLElement | null>(null);

  const handleChangePage = (event: unknown, newPage: number) => {
    // window.scrollTo({ behavior: "smooth", left: 0, top: 900 });
    if (myRef.current)
      myRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
    setPage(newPage - 1);
  };

  // const handleChangeRowsPerPage = (
  //   event: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   setRowsPerPage(parseInt(event.target.value, 10));
  //   setPage(0);
  // };

  useEffect(() => {
    setFilteredRows(
      rows.filter((row) =>
        (
          Object.entries(selectedFilterValues) as [
            keyof InventoryData,
            (string | number)[]
          ][]
        ).every(([key, values]) => {
          if (values.length === 0) return true;

          const headCell = headCells.find((i) => i.id == key);
          if (!headCell) {
            return;
          }

          const rowValue = headCell.getValue(row);

          return typeof rowValue === "string" || typeof rowValue === "number"
            ? values.includes(rowValue)
            : false;
        })
      )
    );
  }, [selectedFilterValues, rows]);

  const [visibleRows, setVisibleRows] = useState<InventoryData[]>([]);

  useEffect(() => {
    setVisibleRows(
      filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    );
  }, [filteredRows, page, rowsPerPage]);

  return (
    <Box>
      <Box ref={myRef} sx={{ p: 2 }}>
        <Typography ref={myRef} variant="h5">
          {t("inventory.products")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          pl: 2,
          pr: 2,
        }}
      >
        <Checkbox
          edge="start"
          indeterminate={
            getSelectedRows(filteredRows).length > 0 &&
            getSelectedRows(filteredRows).length < filteredRows.length
          }
          checked={
            filteredRows.length! > 0 &&
            getSelectedRows(filteredRows).length == filteredRows.length
          }
          onChange={handleSelectAllCheckbox}
        />
        <Typography>{t("inventory.selectAll")}</Typography>
      </Box>

      <Divider />

      {visibleRows &&
        visibleRows.map((row) => {
          return (
            <Box key={`${row.productId}_phoneData`}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  p: 2,
                  bgcolor: row.isSelected ? "hsl(170, 43%, 95%)" : "white",
                }}
              >
                {Object.keys(row).map((key) => {
                  const foundHeadCell = headCells.find((h) => h.id == key);
                  if (foundHeadCell == undefined) return;
                  if (foundHeadCell.id == "actions") return;
                  
                  let cellValue = foundHeadCell.formatValue
                    ? foundHeadCell.formatValue(row)
                    : foundHeadCell.getValue(row);

                  const statusColor =
                    foundHeadCell.id == "status"
                      ? getProductStatusColor(row)
                      : "unset";
                  return (
                    <Box
                      key={`${row.productId}_${foundHeadCell.id}`}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Typography variant="body2">
                        {t(foundHeadCell.label)}
                      </Typography>
                      <Typography variant="body1" sx={{ color: statusColor }}>
                        {cellValue}
                      </Typography>
                    </Box>
                  );
                })}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                  }}
                >
                  <Typography variant="body2">{t("inventory.edit")}</Typography>
                  <IconButton
                    sx={{ alignSelf: "flex-start" }}
                    onClick={() => setSelectedProduct(row)}
                  >
                    <EditRounded />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={() => handleCheckboxClick(row.productId)}
                >
                  <Checkbox
                    edge="start"
                    checked={row.isSelected == true ? true : false}
                  />
                  <Typography>{t("inventory.select")}</Typography>
                </Box>
              </Box>

              <Divider />
            </Box>
          );
        })}
      <Pagination
        showFirstButton
        showLastButton
        defaultValue={1}
        count={Math.ceil(filteredRows.length / rowsPerPage)}
        page={page + 1}
        onChange={handleChangePage}
        size="large"
        sx={{
          display: "flex",
          justifyContent: "center",
          p: 2,
        }}
      />
    </Box>
  );
};

export default InventoryPhoneData;
